import React, { useMemo, useState } from "react";
import { Box, Typography, Stack, Dialog, DialogTitle, DialogContent, DialogActions, Button, Menu, MenuItem, ListItemIcon, ListItemText, Grid, Paper, Fab } from "@mui/material";
import BuildIcon from "@mui/icons-material/Build";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import StyledDataGrid from "../../shared/StyledDataGrid";
import { MimshakPensionService } from "../../services/mimshakPensionService";
import Check from "@mui/icons-material/Check";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useContactNameColumn } from "../../hooks/useContactNameColumn";
import { MimshakData } from "../../interfaces/DTO/mimshakData";
import { formatRelationType, formatBeneficiaryType } from "../../utils/pensionEnumFormatter";
import { formatCurrency } from "../../utils/namesFormater";
import { handlerCodeColumn, adjustedProductTypeColumn, currencyColumn } from "../../shared/UIParts/columnsFormatter";
import { BeneficiaryInfoCard } from "../PersonFinancialData/Insurance/Cards/BeneficiaryInfoCard";
import { BeneficiaryInfo, InsuranceType } from "../../interfaces/DTO/mislaka_xml_insurence_models";
import { BeneficiariesMetrics } from "./BeneficiariesMetrics";
import { calculateBeneficiariesMetrics } from "../../services/beneficiariesMetricsService";
import BeneficiaryUpdateDialog, { BeneficiaryFund } from "./BeneficiaryUpdateDialog";
import ContactBeneficiariesTab from "../CRM/contacts/beneficiaries/ContactBeneficiariesTab";
import { useContacts } from "../../hooks/crm/useContacts";
import { Contact } from "../../interfaces/DTO/crm/contact";
import { matchesDefinition } from "../../utils/beneficiariesUtil";
import { formatName } from "../../utils/namesFormater";

interface BeneficiariesTableProps {
  mimshakData: MimshakData[];
  viewedType?: "USER" | "FAMILY";
}

const BeneficiariesTable: React.FC<BeneficiariesTableProps> = ({ mimshakData, viewedType }) => {
  const contactNameColumn = useContactNameColumn();
  const [selectedBeneficiaries, setSelectedBeneficiaries] = useState<BeneficiaryInfo[] | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null);
  const [beneficiaryUpdateOpen, setBeneficiaryUpdateOpen] = useState(false);
  const { data: allContacts = [] } = useContacts();

  // the table's clients - each one's desired beneficiaries definition (Contact.data.beneficiaries)
  const tableContacts = useMemo(() => {
    const ids = new Set(mimshakData.map((md) => md.contact_id));
    return allContacts.filter((c): c is Contact & { id: number } => c.id != null && ids.has(c.id));
  }, [allContacts, mimshakData]);
  const definitionByContact = useMemo(
    () => new Map(tableContacts.map((c) => [c.id, c.data?.beneficiaries ?? []])),
    [tableContacts]
  );
  const hasAnyDefinition = tableContacts.some((c) => (c.data?.beneficiaries?.length ?? 0) > 0);

  // קבלת רשימת מוטבים ייחודיים (מסננים יורשים חוקיים)
  const distinctBeneficiaries = useMemo(() => {
    const products = mimshakData.flatMap(item => item.data?.products || []);
    const allBeneficiaries = MimshakPensionService.getBeneficiaryInfoDistinct(products);
    // סינון מוטבים שהם לא יורשים חוקיים (3 = LEGAL_HEIRS)
    return allBeneficiaries.filter(b => b.identification_type !== 3);
  }, [mimshakData]);

  // חישוב מטריקות באמצעות הפונקציה המרכזית
  const calculatedMetrics = useMemo(() => {
    return calculateBeneficiariesMetrics(mimshakData, distinctBeneficiaries);
  }, [mimshakData, distinctBeneficiaries]);

  // בניית השורות
  const rows = useMemo(() => {

    const calculatedRows = mimshakData.flatMap((item, itemIndex) => {
      const products = item.data?.products || [];
      const filteredProducts = MimshakPensionService.filterInsuranceCandidates(products);

      return filteredProducts.flatMap((product, productIndex) =>
        product.policies_or_accounts.map((policyOrAccount, policyIndex) => {
          const productBeneficiaries = (policyOrAccount.insurence_coverages?.flatMap(
            coverage => coverage.beneficiaries
          ) || []).filter(b => !MimshakPensionService.isJunkBeneficiary(b));

          // השתמש ב-funds_sum מה-MimshakData
          const fundSum = item.funds_sum || 0;

          // חישוב סכום כיסוי למקרה מוות - חד פעמי
          let deathCoverageAmount = 0;
          const coverages = policyOrAccount.insurence_coverages || [];
          const updateDate = policyOrAccount.update_date;

          coverages.forEach(cov => {
            const details = cov.coverage_details;
            if (!details) return;

            // סינון כיסויים שפגו תוקף - לא נכלול אותם בסכום הביטוח
            const paymentStopDate = details.insurance_fees?.payment_stop_date;
            if (paymentStopDate && updateDate && new Date(paymentStopDate) < new Date(updateDate)) {
              return; // כיסוי פג תוקף - דלג עליו
            }

            const kind = details.insurance_kind;
            const amount = details.insurance_sum ?? 0;

            // אם זה DEATH או ACCIDENTAL_DEATH - קח את הסכום כמו שהוא (גם אם 0)
            if (kind === InsuranceType.DEATH || kind === InsuranceType.ACCIDENTAL_DEATH) {
              deathCoverageAmount += amount;
            }
          });

          // בדיקה אם יש יורשים חוקיים במוצר (identification_type = 3)
          const hasLegalHeirs = productBeneficiaries.some(b => b.identification_type === 3);
          const legalHeirsBeneficiary = productBeneficiaries.find(b => b.identification_type === 3);

          // יצירת אובייקט עבור כל מוטב עם הנתונים שלו במוצר זה
          const beneficiaryData: { [key: string]: { exists: boolean; relation: number | undefined; percentage: number | undefined; nature: number | undefined } } = {};

          distinctBeneficiaries.forEach(beneficiary => {
            const matchingBeneficiary = productBeneficiaries.find(
              pb => pb.id_number === beneficiary.id_number
            );
            beneficiaryData[beneficiary.id_number || ""] = {
              exists: !!matchingBeneficiary,
              relation: matchingBeneficiary?.relation_type,
              percentage: matchingBeneficiary?.beneficiary_percentage,
              nature: matchingBeneficiary?.beneficiary_nature
            };
          });

          return {
            id: `${itemIndex}-${productIndex}-${policyIndex}`,
            contact_id: item.contact_id,
            handler_code: product.details.handler_code,
            policy_number: policyOrAccount.policy_number,
            product_type: product.details.product_type,
            adjusted_product_type: product.details.adjusted_product_type ?? null,
            fund_sum: fundSum,
            death_coverage_amount: deathCoverageAmount,
            has_legal_heirs: hasLegalHeirs,
            legal_heirs_percentage: legalHeirsBeneficiary?.beneficiary_percentage,
            all_beneficiaries: productBeneficiaries,
            // the fund's row - a beneficiaries update task is built from it
            mimshak_row: item,
            ...beneficiaryData
          };
        })
      );
    });

    return calculatedRows;
  }, [mimshakData, distinctBeneficiaries]);

  // חישוב סיכומים עבור השורות שנבחרו
  const selectedTotals = useMemo(() => {
    if (selectedRows.length === 0) {
      return null;
    }

    const selectedRowsData = rows.filter(row => selectedRows.includes(row.id));

    return {
      count: selectedRowsData.length,
      totalFundSum: selectedRowsData.reduce((sum, row) => sum + (row.fund_sum || 0), 0),
      totalDeathCoverage: selectedRowsData.reduce((sum, row) => sum + (row.death_coverage_amount || 0), 0)
    };
  }, [selectedRows, rows]);

  // בדיקה אם יש בכלל סכומי ביטוח - בודקים ישירות במידע הגולמי
  const hasAnyDeathCoverage = useMemo(() => {
    return mimshakData.some(item => {
      const products = item.data?.products || [];
      return products.some(product =>
        product.policies_or_accounts.some(policy => {
          const coverages = policy.insurence_coverages || [];
          const updateDate = policy.update_date;

          return coverages.some(cov => {
            const details = cov.coverage_details;
            if (!details) return false;

            // סינון כיסויים שפגו תוקף
            const paymentStopDate = details.insurance_fees?.payment_stop_date;
            if (paymentStopDate && updateDate && new Date(paymentStopDate) < new Date(updateDate)) {
              return false; // כיסוי פג תוקף - לא סופרים אותו
            }

            const kind = details.insurance_kind;
            const amount = details.insurance_sum ?? 0;
            return (kind === InsuranceType.DEATH || kind === InsuranceType.ACCIDENTAL_DEATH) && amount > 0;
          });
        })
      );
    });
  }, [mimshakData]);

  // בדיקה אם יש יורשים חוקיים
  const hasAnyLegalHeirs = useMemo(() => {
    return rows.some(row => row.has_legal_heirs);
  }, [rows]);

  // בניית העמודות
  const columns = useMemo<GridColDef[]>(() => {
    const baseCols: GridColDef[] = [
      handlerCodeColumn(),
      { field: "policy_number", headerName: "מספר פוליסה", width: 120 },
      adjustedProductTypeColumn(),
      currencyColumn("fund_sum", "סכום צבירה", 120, true)
    ];

    // הוסף עמודת סכום ביטוח רק אם יש סכומי ביטוח
    if (hasAnyDeathCoverage) {
      baseCols.push(currencyColumn("death_coverage_amount", "סכום ביטוח נוסף", 150, true));
    }

    // הוספת עמודה לכל מוטב
    const beneficiaryCols: GridColDef[] = distinctBeneficiaries.map((beneficiary) => {
      const firstName = beneficiary.first_name || "";
      const lastName = beneficiary.last_name || "";
      // אם שם פרטי זהה לשם משפחה, הצג רק את השם הפרטי
      const beneficiaryName = firstName && lastName && firstName === lastName
        ? firstName
        : `${firstName} ${lastName}`.trim() || beneficiary.id_number || "מוטב";

      return {
        field: beneficiary.id_number || "",
        headerName: beneficiaryName,
        width: 150,
        renderCell: (params: GridRenderCellParams) => {
          const data = params.value as { exists: boolean; relation: number | undefined; percentage: number | undefined; nature: number | undefined };

          if (!data || !data.exists) {
            return <Box />;
          }

          // בדיקה אם זה מוטב למקרה חיים (nature = 1)
          const isLifeBeneficiary = data.nature === 1;
          const checkColor = isLifeBeneficiary ? "#ffa726" : "green"; // כתום/צהוב למקרה חיים, ירוק למקרה מוות
          const textColor = isLifeBeneficiary ? "#f57c00" : "text.primary";

          return (
            <Stack direction="column" spacing={0.5} sx={{ py: 0.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Check fontSize="small" sx={{ color: checkColor }} />
              </Box>
              {data.relation != null && (
                <Typography variant="caption" sx={{ textAlign: "center", fontSize: "0.7rem", color: textColor }}>
                  {formatRelationType(data.relation)}
                </Typography>
              )}
              {data.nature != null && (
                <Typography variant="caption" sx={{ textAlign: "center", fontSize: "0.7rem", color: textColor, fontWeight: 500 }}>
                  {formatBeneficiaryType(data.nature)}
                </Typography>
              )}
              {data.percentage != null && (
                <Typography variant="caption" sx={{ textAlign: "center", fontSize: "0.7rem", fontWeight: 600, color: textColor }}>
                  {data.percentage}%
                </Typography>
              )}
            </Stack>
          );
        }
      };
    });

    // עמודה ליורשים חוקיים
    const legalHeirsColumn: GridColDef = {
      field: "has_legal_heirs",
      headerName: "יורשים חוקיים",
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const hasLegalHeirs = params.value as boolean;
        const percentage = params.row.legal_heirs_percentage;

        if (!hasLegalHeirs) {
          return <Box />;
        }

        return (
          <Stack direction="column" spacing={0.5} sx={{ py: 0.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Check fontSize="small" sx={{ color: "green" }} />
            </Box>
            {percentage && (
              <Typography variant="caption" sx={{ textAlign: "center", fontSize: "0.7rem", fontWeight: 600 }}>
                {percentage}%
              </Typography>
            )}
          </Stack>
        );
      }
    };

    // the fund's death beneficiaries vs the client's desired definition
    const matchColumn: GridColDef = {
      field: "matches_definition",
      headerName: "תואם להגדרה",
      width: 120,
      sortable: false,
      // a grey column (see the grid's sx)
      cellClassName: "match-definition-col",
      headerClassName: "match-definition-col",
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const definition = definitionByContact.get(params.row.contact_id) ?? [];
        if (definition.length === 0)
          return (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
              <Typography variant="caption" color="text.secondary">אין הגדרה</Typography>
            </Box>
          );
        const matches = matchesDefinition(params.row.all_beneficiaries ?? [], definition);
        return matches ? (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75, height: "100%" }}>
            <CheckCircleIcon fontSize="small" sx={{ color: "success.main" }} />
            <Typography variant="body2" fontWeight={600} color="success.main">
              תואם
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="warning.main" sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
            לא תואם
          </Typography>
        );
      },
    };

    const allColumns = viewedType === "FAMILY"
      ? [contactNameColumn, ...baseCols, ...(hasAnyDefinition ? [matchColumn] : []), ...beneficiaryCols]
      : [...baseCols, ...(hasAnyDefinition ? [matchColumn] : []), ...beneficiaryCols];

    // הוסף עמודת יורשים חוקיים רק אם יש יורשים חוקיים
    if (hasAnyLegalHeirs) {
      allColumns.push(legalHeirsColumn);
    }

    return allColumns;
  }, [distinctBeneficiaries, viewedType, contactNameColumn, hasAnyDeathCoverage, hasAnyLegalHeirs, definitionByContact, hasAnyDefinition]);

  // פונקציה לטיפול בלחיצה על שורה
  const handleRowClick = (params: any) => {
    const beneficiaries = params.row.all_beneficiaries as BeneficiaryInfo[];
    if (beneficiaries && beneficiaries.length > 0) {
      setSelectedBeneficiaries(beneficiaries);
      setDialogOpen(true);
    }
  };

  // the funds a beneficiaries update can be applied to
  const funds: BeneficiaryFund[] = useMemo(
    () =>
      rows.map((row) => ({
        key: row.id,
        contact_id: row.contact_id,
        policy_number: row.policy_number,
        handler_code: row.handler_code,
        product_type: row.product_type,
        adjusted_product_type: row.adjusted_product_type,
        beneficiaries: row.all_beneficiaries,
        row: row.mimshak_row,
      })),
    [rows]
  );

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedBeneficiaries(null);
  };

  if (rows.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          לא נמצאו מוטבים
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", p: 2, direction: "rtl" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6" sx={{ textAlign: "right" }}>
          טבלת מוטבים
        </Typography>
        {/* the same look as the management tab's actions button (PolicyActionsButton) */}
        <Fab variant="extended" color="primary" size="medium" onClick={(e) => setActionsAnchor(e.currentTarget)}>
          <BuildIcon sx={{ ml: 1 }} />
          פעולות
        </Fab>
        <Menu anchorEl={actionsAnchor} open={!!actionsAnchor} onClose={() => setActionsAnchor(null)} dir="rtl">
          <MenuItem
            onClick={() => {
              setActionsAnchor(null);
              setBeneficiaryUpdateOpen(true);
            }}
          >
            <ListItemIcon>
              <VolunteerActivismIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="עדכון מוטבים" />
          </MenuItem>
        </Menu>
      </Box>

      {/* the desired beneficiaries definition - each client's (view / edit) */}
      {tableContacts.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            הגדרת מוטבים רצויה
          </Typography>
          <Grid container spacing={2} alignItems="stretch">
            {tableContacts.map((c) => (
              <Grid item xs={12} md={tableContacts.length > 1 ? 6 : 12} key={c.id} sx={{ display: "flex" }}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, width: "100%" }}>
                  {tableContacts.length > 1 && (
                    <Typography fontWeight={600} sx={{ mb: 1 }}>
                      {formatName(c)}
                    </Typography>
                  )}
                  <ContactBeneficiariesTab contact={c} spouse={tableContacts.find((o) => o.id !== c.id)} />
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* מטריקות */}
      <BeneficiariesMetrics
        distinctBeneficiaries={distinctBeneficiaries}
        beneficiaryTotals={calculatedMetrics.beneficiaryTotals}
        beneficiaryDeathCoverageTotals={calculatedMetrics.beneficiaryDeathCoverageTotals}
        totalWithoutBeneficiaries={calculatedMetrics.totalWithoutBeneficiaries}
        totalWithoutBeneficiariesDeathCoverage={calculatedMetrics.totalWithoutBeneficiariesDeathCoverage}
        totalLegalHeirs={calculatedMetrics.totalLegalHeirs}
        totalLegalHeirsDeathCoverage={calculatedMetrics.totalLegalHeirsDeathCoverage}
      />

      <StyledDataGrid
        rows={rows}
        columns={columns}
        maxHeight="600px"
        pageSizeOptions={[10, 25, 50]}
        rowHeight={80}
        onRowClick={handleRowClick}
        checkboxSelection
        disableRowSelectionOnClick
        rowSelectionModel={selectedRows}
        onRowSelectionModelChange={(newSelection) =>
          setSelectedRows(newSelection as string[])
        }
        hideFooter={rows.length <= 100 && selectedRows.length === 0}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer'
          },
          '& .match-definition-col': {
            backgroundColor: 'action.hover',
          },
        }}
      />

      {/* שורת סיכום */}
      {selectedTotals && (
        <Box
          sx={{
            mt: 2,
            display: "flex",
            flexWrap: "wrap",
            gap: 4,
            px: 2,
          }}
        >
          <Box>
            מספר פוליסות: <strong>{selectedTotals.count}</strong>
          </Box>
          {selectedTotals.totalFundSum > 0 && (
            <Box>
              סה"כ סכום:{" "}
              <strong>
                <span dir="ltr">{formatCurrency(selectedTotals.totalFundSum)}</span>
              </strong>
            </Box>
          )}
          {selectedTotals.totalDeathCoverage > 0 && (
            <Box>
              סה"כ סכום ביטוח נוסף:{" "}
              <strong>
                <span dir="ltr">{formatCurrency(selectedTotals.totalDeathCoverage)}</span>
              </strong>
            </Box>
          )}
        </Box>
      )}

      <BeneficiaryUpdateDialog open={beneficiaryUpdateOpen} onClose={() => setBeneficiaryUpdateOpen(false)} funds={funds} />

      {/* דיאלוג להצגת פרטי מוטבים */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            direction: 'rtl'
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'right' }}>
          פרטי מוטבים במוצר
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {selectedBeneficiaries?.map((beneficiary, index) => (
              <BeneficiaryInfoCard key={index} beneficiary={beneficiary} />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} variant="contained">
            סגור
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BeneficiariesTable;
